import type { TFunction } from 'i18next';

import type { FilterDef, FilterOption } from '@nucleus-commerce/admin-kit/index-table';
import {
  OrganizationStatus,
  type OrganizationListItem,
} from '@nucleus-commerce/api-client/platform';

/** Fields GET /organizations sorts on; anything else is a 400. */
export const ORGANIZATION_SORTS = ['name', 'createdAt'] as const;

const valuesOf = (values: Record<string, string>): FilterOption[] =>
  Object.values(values).map((value) => ({ value, label: value }));

/**
 * The filters as the URL and the API spell them, for the route's search schema and loader. The
 * enum `options` drop unknown values the same way the page's filters do, so the prefetch and the
 * page share a query key. `createdAt` days are UTC days: an organization has no store timezone.
 */
export const ORGANIZATION_FILTER_FIELDS = [
  { key: 'status', type: 'enum', options: valuesOf(OrganizationStatus) },
  { key: 'createdAt', type: 'date-range' },
] as const;

export interface OrganizationFiltersOptions {
  /** A `t` that reads both `organizations` and `common` keys. */
  t: TFunction;
}

/** Filters of the organizations table, in the order the filter menu lists them. */
export function organizationFilters({
  t,
}: OrganizationFiltersOptions): Array<FilterDef<OrganizationListItem>> {
  return [
    {
      key: 'status',
      label: t('organizations:filters.status'),
      type: 'enum',
      pinned: true,
      options: valuesOf(OrganizationStatus).map(({ value }) => ({
        value,
        label: t(`common:status.${value}`),
      })),
    },
    {
      key: 'createdAt',
      label: t('organizations:filters.createdAt'),
      type: 'date-range',
      aliases: ['created'],
    },
  ];
}
